import { useState, useEffect, useCallback } from 'react';
import { 
  BarChart3, TrendingUp, Download, FileSpreadsheet, DollarSign, 
  ShoppingCart, Calendar, Utensils, ArrowUpRight, ArrowDownRight, RefreshCcw, Filter
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { format, subDays, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek } from 'date-fns';
import { useAuth } from '@/context/AuthContext';
import { API_URL } from '@/config/api';

const CHART_COLORS = ['#d97706', '#0891b2', '#7c3aed', '#059669', '#dc2626', '#2563eb', '#f59e0b', '#10b981'];

const PERIOD_PRESETS = [
  { label: 'Today', value: 'today' },
  { label: 'This Week', value: 'week' },
  { label: 'Last 7 Days', value: '7' },
  { label: 'Last 30 Days', value: '30' },
  { label: 'This Month', value: 'month' },
  { label: 'Last Month', value: 'last_month' },
  { label: 'Last 90 Days', value: '90' },
  { label: 'Custom Range', value: 'custom' },
];

function getPeriodDates(period) {
  const today = new Date();
  switch (period) {
    case 'today': return { start: format(today, 'yyyy-MM-dd'), end: format(today, 'yyyy-MM-dd') };
    case 'week': return { start: format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'), end: format(endOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd') };
    case 'month': return { start: format(startOfMonth(today), 'yyyy-MM-dd'), end: format(endOfMonth(today), 'yyyy-MM-dd') };
    case 'last_month': { const lm = subMonths(today, 1); return { start: format(startOfMonth(lm), 'yyyy-MM-dd'), end: format(endOfMonth(lm), 'yyyy-MM-dd') }; }
    case '7': return { start: format(subDays(today, 7), 'yyyy-MM-dd'), end: format(today, 'yyyy-MM-dd') };
    case '30': return { start: format(subDays(today, 30), 'yyyy-MM-dd'), end: format(today, 'yyyy-MM-dd') };
    case '90': return { start: format(subDays(today, 90), 'yyyy-MM-dd'), end: format(today, 'yyyy-MM-dd') };
    default: return { start: format(subDays(today, 30), 'yyyy-MM-dd'), end: format(today, 'yyyy-MM-dd') };
  }
}

export default function AdminReports() {
  const { token, user } = useAuth();
  const [report, setReport] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [exporting, setExporting] = useState(false);

  const canSeeFinancials = ['super_admin', 'manager', 'finance', 'operations_manager'].includes(user?.role);

  const getDateRange = () => {
    if (period === 'custom' && customStart && customEnd) {
      return { start: customStart, end: customEnd };
    }
    return getPeriodDates(period);
  };

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange();
      const days = period !== 'custom' && !isNaN(period) ? parseInt(period) : 30;

      const [orderRes, analyticsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/reports/orders?start_date=${start}&end_date=${end}`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        }),
        fetch(`${API_URL}/api/admin/reports/analytics?days=${days}`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        })
      ]);

      if (orderRes.ok) setReport(await orderRes.json());
      if (analyticsRes.ok) setAnalyticsData(await analyticsRes.json());
    } catch (error) {
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, [token, period, customStart, customEnd]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const exportCSV = () => {
    if (!report?.orders?.length) { toast.error('No data to export'); return; }
    setExporting(true);
    try {
      const headers = ['Order ID', 'Date', 'Customer', 'Items', 'Total (R)', 'Status', 'Payment'];
      const rows = report.orders.map(o => [
        o.order_id.slice(-8).toUpperCase(),
        (o.created_at || '').substring(0, 10),
        o.customer_name || o.customer_email || 'Unknown',
        o.items?.map(i => `${i.name} x${i.quantity}`).join('; ') || '',
        o.total_amount?.toFixed(2) || '0.00',
        o.status || '',
        o.payment_status || ''
      ]);
      const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gc3h-sales-report-${getDateRange().start}-to-${getDateRange().end}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Report exported successfully');
    } finally {
      setExporting(false);
    }
  };

  const { start, end } = getDateRange();
  const dailyData = (report?.daily_revenue || []).map(d => ({ date: d.date.slice(5), revenue: d.revenue, orders: d.orders }));
  const topItemsData = (report?.top_items || []).slice(0, 8);
  // Use actual backend field names: hourly_distribution (array) from analytics endpoint
  const analyticsHourly = (analyticsData?.hourly_distribution || []).map(h => ({ hour: h.hour, orders: h.orders }));
  // Build status breakdown from the report orders
  const statusCounts = {};
  (report?.orders || []).forEach(o => { const s = o.status || 'unknown'; statusCounts[s] = (statusCounts[s] || 0) + 1; });
  const ordersByStatus = Object.entries(statusCounts).map(([s, v]) => ({ name: s.replace(/_/g, ' '), value: v }));

  return (
    <div data-testid="admin-reports">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-secondary-900 flex items-center gap-2">
            <BarChart3 className="h-7 w-7 text-primary" />
            Sales Reports
          </h1>
          <p className="text-muted-foreground mt-1">Detailed sales analysis with period selection</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={fetchReports} disabled={loading} size="sm">
            <RefreshCcw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {canSeeFinancials && (
            <Button onClick={exportCSV} disabled={exporting || !report?.orders?.length} size="sm" className="bg-green-600 hover:bg-green-700 text-white">
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          )}
        </div>
      </div>

      {/* Period Selector */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Filter className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <div className="flex flex-wrap gap-2">
              {PERIOD_PRESETS.map(p => (
                <button
                  key={p.value}
                  onClick={() => setPeriod(p.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    period === p.value ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {period === 'custom' && (
              <div className="flex items-center gap-2 ml-2">
                <input
                  type="date"
                  value={customStart}
                  onChange={e => setCustomStart(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-border rounded-lg"
                />
                <span className="text-muted-foreground">to</span>
                <input
                  type="date"
                  value={customEnd}
                  onChange={e => setCustomEnd(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-border rounded-lg"
                />
              </div>
            )}

            <span className="text-xs text-muted-foreground ml-auto">
              Period: {start} → {end}
            </span>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Summary KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total Revenue', value: `R${(report?.total_revenue || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`, icon: DollarSign, color: 'text-green-600', bg: 'bg-green-100', show: canSeeFinancials },
              { label: 'Orders', value: report?.total_orders || 0, icon: ShoppingCart, color: 'text-blue-600', bg: 'bg-blue-100', show: true },
              { label: 'Avg Order Value', value: `R${(report?.avg_order_value || 0).toFixed(2)}`, icon: TrendingUp, color: 'text-amber-600', bg: 'bg-amber-100', show: canSeeFinancials },
              { label: 'Menu Items Sold', value: report?.top_items?.reduce((s, i) => s + i.quantity, 0) || 0, icon: Utensils, color: 'text-purple-600', bg: 'bg-purple-100', show: true },
            ].filter(s => s.show).map((stat) => (
              <Card key={stat.label}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-muted-foreground">{stat.label}</span>
                    <div className={`p-2 rounded-lg ${stat.bg}`}>
                      <stat.icon className={`h-4 w-4 ${stat.color}`} />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-secondary-900">{stat.value}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {canSeeFinancials && dailyData.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <TrendingUp className="h-5 w-5 text-green-500" />
                    Revenue Trend
                  </CardTitle>
                  <CardDescription>Daily revenue over the selected period</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={dailyData}>
                      <defs>
                        <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#059669" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#059669" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                      <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={v => `R${v}`} />
                      <Tooltip formatter={v => [`R${v.toLocaleString()}`, 'Revenue']} contentStyle={{ borderRadius: 8 }} />
                      <Area type="monotone" dataKey="revenue" stroke="#059669" strokeWidth={2} fill="url(#revGrad)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {topItemsData.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Utensils className="h-5 w-5 text-amber-500" />
                    Top Selling Items
                  </CardTitle>
                  <CardDescription>By revenue in selected period</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={topItemsData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={v => `R${v}`} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} stroke="#94a3b8" width={110} />
                      <Tooltip formatter={(v, n) => [n === 'revenue' ? `R${v.toFixed(2)}` : v, n === 'revenue' ? 'Revenue' : 'Qty']} contentStyle={{ borderRadius: 8 }} />
                      <Bar dataKey="revenue" fill="#d97706" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {ordersByStatus.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <ShoppingCart className="h-5 w-5 text-blue-500" />
                    Orders by Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie data={ordersByStatus} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={4} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                        {ordersByStatus.map((_, i) => (
                          <Cell key={`c-${i}`} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: 8 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {analyticsHourly.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Calendar className="h-5 w-5 text-purple-500" />
                    Peak Ordering Hours
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={analyticsHourly}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="hour" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                      <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                      <Tooltip formatter={v => [v, 'Orders']} contentStyle={{ borderRadius: 8 }} />
                      <Bar dataKey="orders" fill="#7c3aed" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Detailed Orders Table */}
          {canSeeFinancials && report?.orders?.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <FileSpreadsheet className="h-5 w-5 text-slate-500" />
                      Order Details — {report.orders.length} orders
                    </CardTitle>
                    <CardDescription>Showing up to 200 most recent paid orders</CardDescription>
                  </div>
                  <Badge variant="outline">{start} to {end}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th className="text-left p-3 font-medium text-muted-foreground">Order</th>
                        <th className="text-left p-3 font-medium text-muted-foreground">Date</th>
                        <th className="text-left p-3 font-medium text-muted-foreground">Customer</th>
                        <th className="text-left p-3 font-medium text-muted-foreground">Items</th>
                        <th className="text-right p-3 font-medium text-muted-foreground">Total</th>
                        <th className="text-center p-3 font-medium text-muted-foreground">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {report.orders.map((order) => (
                        <tr key={order.order_id} className="hover:bg-muted/30 transition-colors">
                          <td className="p-3 font-mono text-xs text-muted-foreground">{order.order_id.slice(-8).toUpperCase()}</td>
                          <td className="p-3 text-sm">{(order.created_at || '').substring(0, 10)}</td>
                          <td className="p-3">
                            <div className="font-medium">{order.customer_name || 'Unknown'}</div>
                            <div className="text-xs text-muted-foreground">{order.customer_email}</div>
                          </td>
                          <td className="p-3 max-w-[200px]">
                            <p className="text-xs text-muted-foreground truncate">
                              {order.items?.map(i => `${i.name} ×${i.quantity}`).join(', ')}
                            </p>
                          </td>
                          <td className="p-3 text-right font-semibold text-primary">R{order.total_amount?.toFixed(2)}</td>
                          <td className="p-3 text-center">
                            <Badge className={`text-xs ${order.status === 'collected' ? 'bg-emerald-100 text-emerald-800' : order.status === 'cancelled' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
                              {order.status?.replace(/_/g, ' ')}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {(!report?.orders?.length) && (
            <Card>
              <CardContent className="p-12 text-center">
                <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground font-medium">No paid orders in this period</p>
                <p className="text-sm text-muted-foreground mt-1">Try a different date range</p>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
